/* LOCAL MEDIA BYTES — IndexedDB, Blobs, no base64.

   Every other asset in this app travels as a `data:` URL inside the document:
   an image, a model, the reference photo. That works because those are small
   enough that re-serialising the whole document is cheap, and it buys a real
   property — a downloaded `.json` is self-contained.

   A clip is not small enough. services/projects-fs.js writes a local project
   with `JSON.stringify(record, null, 2)` of the ENTIRE document on every save,
   rename and duplicate, and listLocal() parses every file in the folder to
   build the project list. A 25 MB clip is ~34 MB of base64, so a handful of
   video projects in one folder turns opening the project list into parsing
   hundreds of megabytes. The document has to stay small, and that means the
   bytes have to live somewhere else.

   So: a second database, holding Blobs. The document keeps a `blobId` — a
   string — and a video object stays about 200 bytes of JSON. projects-fs.js
   and projects-db.js are then completely unchanged, which is the point.

   CONTENT-ADDRESSED, by the same sha-256 assets.js uploads with. Duplicating a
   project, undoing across a clip swap, or two objects using the same file all
   cost zero extra bytes, and the id is stable across sessions and machines —
   so the same clip uploaded from two devices dedupes server-side too.

   Same house style as projects-db.js: one object store, one transaction per
   exported function, nothing held across an await. */

import { checksum } from './assets.js';

const DB_NAME = 'illusio-media';
const STORE = 'blobs';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE, { keyPath: 'id' }); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const asPromise = r => new Promise((resolve, reject) => {
  r.onsuccess = () => resolve(r.result);
  r.onerror = () => reject(r.error);
});

/* One connection, reused. openDB() per call worked, but it made every
   operation its own connection and widened the window in which two of them
   could be in flight against each other. */
let dbPromise = null;
const db = () => (dbPromise ||= openDB());

/**
 * A WRITE resolves when the transaction COMMITS, not when the request
 * succeeds — those are different moments, and the gap between them was a real
 * bug: putBlob returned, the caller immediately opened a fresh readonly
 * transaction to read the same record back, and got nothing, because the
 * write had not landed yet. Downstream that surfaced as a clip whose bytes
 * "are not on this device" — a black plane on the FIRST import of a file and
 * a correct one on the second, which is exactly as confusing as it sounds.
 */
async function withStore(mode, fn) {
  const conn = await db();
  const tx = conn.transaction(STORE, mode);
  const out = fn(tx.objectStore(STORE));
  if (mode !== 'readwrite') return out;
  const committed = new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('the write was aborted'));
  });
  const value = await out;
  await committed;
  return value;
}

/**
 * Store a Blob and return its content id. Storing the same bytes twice is a
 * no-op that returns the same id, which is what makes duplicate-a-project free.
 *
 * The Blob goes in as a Blob — IndexedDB stores it through structured clone,
 * and Chrome keeps a large one on disk rather than on the JS heap. Nothing is
 * ever base64'd, so a 25 MB clip costs 25 MB, not 34.
 *
 * @param {Blob|File} blob
 * @param {object} [meta]  mime/duration/dimensions, for a future sweep UI
 * @returns {Promise<string>} sha-256 hex
 */
export async function putBlob(blob, meta = {}) {
  const id = await checksum(blob);
  const existing = await withStore('readonly', s => asPromise(s.get(id)));
  if (existing) return id;
  await withStore('readwrite', s => asPromise(s.put({
    id, blob, bytes: blob.size, mime: blob.type || '', addedAt: Date.now(), ...meta
  })));
  return id;
}

/** The bytes back, or null if this browser has never seen them. */
export async function getBlob(id) {
  if (!id) return null;
  const row = await withStore('readonly', s => asPromise(s.get(id)));
  return row ? row.blob : null;
}

/** What is being held locally, for a storage readout. */
export async function usage() {
  const rows = await withStore('readonly', s => asPromise(s.getAll()));
  return { count: rows.length, bytes: rows.reduce((n, r) => n + (r.bytes || 0), 0) };
}

/**
 * Drop every blob no local project still points at.
 *
 * Deliberately NOT called when an object is deleted: undo would then have
 * nothing to restore, and the bytes are the one thing history cannot vault
 * cheaply. Sweeping is a maintenance action over the whole local library, run
 * with the full set of ids that are still referenced — never a guess made from
 * one document.
 *
 * @param {Iterable<string>} keepIds
 */
export async function sweep(keepIds) {
  const keep = new Set(keepIds);
  const rows = await withStore('readonly', s => asPromise(s.getAll()));
  const doomed = rows.filter(r => !keep.has(r.id)).map(r => r.id);
  for (const id of doomed) await withStore('readwrite', s => asPromise(s.delete(id)));
  return doomed.length;
}
